/**
 * Hook: read the latest paste-box analysis row for `(candidate × jd × stage)`.
 * Phase 2.6.
 *
 * Backend canon: the paste-box save/skip/regenerate endpoints all return
 * the canonical `InterviewAnalysisPasteBoxResponse` shape (see
 * `app/schemas/interview_analysis.py`). The orchestrator caches the most
 * recent save/skip response under `["interview-analysis", "paste-box", ...]`
 * so subsequent renders read from cache without an extra GET.
 *
 * v1 of this hook is cache-only: it reads from the cache populated by
 * the mutation hooks (`useStartInterviewAnalysisParse`, `useSkipAnalysis`,
 * `useRegenerateAnalysisParse`) and seeded by the existing
 * `useInterviewAnalysis({type: "post"})` query when a complete row is
 * present.
 *
 * The seed-from-existing path is the page-load reconciliation:
 *   - If the candidate has a Phase-2.5 `interview_analyses` row with
 *     status='complete' AND it carries `stage_id` (added in 2.6 migration
 *     0054), it shows up via `useInterviewAnalysis`. Consumer is
 *     responsible for seeding this cache before consuming the hook.
 *
 * Note: there is no GET-by-resource endpoint for paste-box on the
 * backend in this phase. The mutation responses populate the cache
 * directly. Consumers seed `undefined` → empty paste-box state.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";

export interface AnalysisPasteBoxRow {
  id: string;
  candidate_id: string;
  jd_id: string | null;
  stage_id: string | null;
  /**
   * Per-round dimension (backend migration 0124). `null` = unassigned/legacy;
   * optional (absent on rows serialized before the field existed).
   */
  round_id?: string | null;
  skipped: boolean;
  transcript: string | null;
  analysis: string | null;
  prompt_version_id: string | null;
  status: string;
  /**
   * Phase 2.6.1 (B5) — client-side saved-at ISO timestamp.
   *
   * Populated by the save/skip/regenerate mutation hooks at the moment
   * the success handler fires (the backend's paste-box response does
   * NOT carry a timestamp field in this phase, by spec lock). Surfaced
   * to the saved view as `savedAtIso` so the "Saved {date}"
   * sub-line renders per spec §7.4.
   *
   * Hydrated rows from a prior session (none in v1 — no GET endpoint)
   * would land here `null`; the consumer renders no sub-line in that
   * case.
   */
  saved_at: string | null;
}

interface UseAnalysisPasteBoxOpts {
  candidateId: string | undefined;
  jdId: string | undefined;
  stageId: string | undefined;
  /** Per-round slot key (backend migration 0124). `null` = unassigned/legacy. */
  roundId?: string | null;
  enabled: boolean;
}

/**
 * Returns the cached paste-box row for `(candidate × jd × stage)`. The
 * mutation hooks populate the cache on save/skip/regenerate; this hook
 * does NOT fetch from the network.
 */
export function useAnalysisPasteBox(
  opts: UseAnalysisPasteBoxOpts,
): UseQueryResult<AnalysisPasteBoxRow | null, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { candidateId, jdId, stageId, enabled } = opts;
  const roundId = opts.roundId ?? null;

  return useQuery<AnalysisPasteBoxRow | null, ApiError>({
    queryKey: [
      "interview-analysis",
      "paste-box",
      tenantSlug,
      candidateId,
      jdId,
      stageId,
      roundId,
    ],
    // Cache-only — no network. queryFn returns `null` if no row is cached.
    queryFn: () => null,
    enabled: enabled && !!tenantSlug && !!candidateId && !!jdId && !!stageId,
    // Never refetch — the mutation hooks own writes.
    staleTime: Infinity,
    refetchInterval: false,
    retry: false,
  });
}
